import FormBase from '@/components/forms/new/FormBase';
import OperatorPlatformNameField from './formFields/OperatorPlatformNameField';
import OperatorPlatformDescShort from './formFields/OperatorPlatformDescShort';
import OperatorPlatformDescLong from './formFields/OperatorPlatformDescLong';
import OperatorPlatformIconLargeURL from './formFields/OperatorPlatformIconLargeURL';
import OperatorPlatformIconSmallURL from './formFields/OperatorPlatformIconSmallURL';
import OperatorPlatformVendorID from './formFields/OperatorPlatformVendorID';
import OperatorPlatformTermsURL from './formFields/OperatorPlatformTermsURL';
import OperatorPlatformTrunkPrefix from './formFields/OperatorPlatformTrunkPrefix';
import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import updateServiceDefinition from 'API/ServiceProfiles/updateServiceDefinition';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceDefinition from 'API/ServiceProfiles/createServiceDefinition';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import useFileUpload from 'hooks/useFileUpload';
import useDetectFileType from 'hooks/useDetectFileType';
import uploadServiceDefinitionLogo from 'API/ServiceProfiles/uploadServiceDefinitionLogo';

import BreadcrumbTrail from 'components/BreadcrumbTrail';

const AddOperatorPlatformServices = ({ mode, data, handleBackButton }) => {
    const { currentWholesaler: wholesalerID } = useSelector(
        (state) => state.navigationLists,
    );
    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);

    const DEFAULT_SERVICE_DEFINITION_PAYLOAD = {
        name: '',
        logoLarge: '',
        logoSmall: '',
        treEnabled: false,
        descriptionShort: '',
        descriptionLong: '',
        termsLink: '',
        visibility: 0,
        trunkSegment: '',
    };

    const [modalState, setModalState] = useState(
        data || DEFAULT_SERVICE_DEFINITION_PAYLOAD,
    );

    const imageBaseURL = import.meta.env.VITE_APP_IMAGE_BASE + '/images/';
    // File handler
    const logoFileRefLarge = useRef(null);
    const {
        uploadHandler: uploadHandlerLarge,
        fileReaderResult: fileReaderResultLarge,
        file: fileLarge,
    } = useFileUpload({
        allowedExtensions: ['.png'],
        logoFileRef: logoFileRefLarge,
    });
    const logoFileTypeLarge = useDetectFileType(
        fileReaderResultLarge || imageBaseURL + modalState?.logoLarge,
    );

    // File handler for small icon
    const logoFileRefSmall = useRef(null);
    const {
        uploadHandler: uploadHandlerSmall,
        fileReaderResult: fileReaderResultSmall,
        file: fileSmall,
    } = useFileUpload({
        allowedExtensions: ['.png'],
        logoFileRef: logoFileRefSmall,
    });
    const logoFileTypeSmall = useDetectFileType(
        fileReaderResultSmall || imageBaseURL + modalState?.logoSmall,
    );

    const extractErrorMessage = (e) =>
        e?.response?.data?.message ||
        e?.message ||
        'An error occurred. Please try again later.';

    const [error, setError] = useState(''); // keep as string
    const [isLoading, setIsLoading] = useState(null);

    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const initialValues = {
        name: modalState?.name || '',
        vendorID: modalState?.vendorID || '',
        logoLarge: modalState?.logoLarge || '',
        logoSmall: modalState?.logoSmall || '',
        trunkPrefix: modalState?.trunkPrefix || '',
        customShortText: modalState?.descriptionShort || '',
        descriptionLong: modalState?.descriptionLong || '',
        termsURL: modalState?.termsLink || '',
        trunkSegment: modalState?.trunkSegment || '',
    };

    const finalFormSubmit = async (values, actions) => {
        setError('');
        setIsLoading(true);
        console.log(modalState);
        const handleFileUpload = (resData) => {
            console.log(resData, modalState);
            if (fileLarge) {
                const formDataLarge = new FormData();
                formDataLarge.append('LogoSize', 1);
                formDataLarge.append('Logo', fileLarge);
                uploadServiceDefinitionLogo({
                    wholesalerId: wholesalerID,
                    serviceDefinitionID: resData?.id || modalState?.id,
                    payload: formDataLarge,
                    scope: requiredScope,
                });
            }
            if (fileSmall) {
                const formDataSmall = new FormData();
                formDataSmall.append('LogoSize', 0);
                formDataSmall.append('Logo', fileSmall);
                uploadServiceDefinitionLogo({
                    wholesalerId: wholesalerID,
                    serviceDefinitionID: resData?.id || modalState?.id,
                    payload: formDataSmall,
                    scope: requiredScope,
                });
            }
        };
        try {
            if (mode === 'Edit') {
                await updateServiceDefinition(
                    modalState,
                    wholesalerID,
                    requiredScope,
                ).then((res) => {
                    handleChangeState(res.data);
                    handleFileUpload(res.data);
                });
            } else {
                await createServiceDefinition(
                    modalState,
                    wholesalerID,
                    requiredScope,
                ).then((res) => {
                    handleChangeState(res.data);
                    handleFileUpload(res.data);
                });
            }
            handleBackButton();
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    return (
        <>
            {error && (
                <Alert status="error" sx={{ marginBottom: '1rem' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${mode} Operator Platform`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="serviceDefinitionProfileBackBtn"
                ufSaveBtn="serviceDefinitionProfileSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <OperatorPlatformVendorID
                            key="vendorID"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <OperatorPlatformNameField
                            key="name"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                console.log(e.target.value);
                                setModalState((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }));
                            }}
                            darkMode={darkMode}
                        />,
                        <OperatorPlatformIconLargeURL
                            key="iconLargeURL"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            uploadHandler={uploadHandlerLarge}
                            fileReaderResult={
                                fileReaderResultLarge ||
                                imageBaseURL + modalState?.logoLarge
                            }
                            logoFileType={logoFileTypeLarge}
                            logoFileRef={logoFileRefLarge}
                        />,
                        <OperatorPlatformIconSmallURL
                            key="iconSmallURL"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            uploadHandler={uploadHandlerSmall}
                            fileReaderResult={
                                fileReaderResultSmall ||
                                imageBaseURL + modalState?.logoSmall
                            }
                            logoFileType={logoFileTypeSmall}
                            logoFileRef={logoFileRefSmall}
                        />,
                        <OperatorPlatformTrunkPrefix
                            key="trunkSegment"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    trunkSegment: e.target.value,
                                }));
                            }}
                        />,
                        <OperatorPlatformDescShort
                            key="descriptionShort"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    descriptionShort: e.target.value,
                                }));
                            }}
                        />,
                        <OperatorPlatformDescLong
                            key="descriptionLong"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    descriptionLong: e.target.value,
                                }));
                            }}
                        />,
                        <OperatorPlatformTermsURL
                            key="termsLink"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    termsLink: e.target.value,
                                }));
                            }}
                        />,
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default AddOperatorPlatformServices;
